import { BrowserModule, BrowserTransferStateModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgxSpinnerModule } from 'ngx-spinner';
import { AgmCoreModule } from '@agm/core';

import { OverlayContainer, Overlay } from '@angular/cdk/overlay';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { CustomOverlayContainer } from './theme/utils/custom-overlay-container';
import { menuScrollStrategy } from './theme/utils/scroll-strategy';

import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';
import { MenuComponent } from './theme/components/menu/menu.component';
import { BreadcrumbComponent } from './theme/components/breadcrumb/breadcrumb.component';

import { AppSettings } from './app.settings';
import { AppInterceptor } from './theme/utils/app-interceptor';
import { OptionsComponent } from './theme/components/options/options.component';

import { AuthGuard } from './shared/guard/auth.guard';
import { LoggedInAuthGuard } from './shared/guard/logged-in-auth.guard';
import { SanitizeHtmlPipe } from './theme/pipes/sanitize-html.pipe';

import { VerifyTokenGuard } from './shared/guard/verify-token.guard';
import { NotificationService } from './shared/services/notification.service';
import { PopupComponent } from './theme/components/popup/popup.component';
import { SubscriptionService } from './shared/services/subscription.service';
import { CommonModule, DecimalPipe, CurrencyPipe, UpperCasePipe, APP_BASE_HREF, } from '@angular/common';
import { ReplacePipe } from './theme/pipes/replace.pipe';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { AppConfigService } from './theme/utils/app-config-service';
import { MatDialogModule } from '@angular/material/dialog';
import { CmsPageComponent } from './theme/components/cms-page/cms-page.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CmsTopBgComponent } from './theme/components/cms-top-bg/cms-top-bg.component';
import { CmsBottomComponent } from './theme/components/cms-bottom/cms-bottom.component';
import { environment } from 'src/environments/environment';
import { FeatureTagResolver } from './shared/resolvers/feature-tag.resolver';
import { Router, Routes } from '@angular/router';
import { AppRoutingModule } from './app.routing';
import { ConfigService } from 'src/app/config.service';
import { GlobalComponent } from './main-theme/global-theme/global.component';
import { UserDetailsResolver } from './shared/resolvers/user-details.service';
import { StoreService } from './shared/resolvers/store.service';
import { GlobalModule } from './main-theme/global-theme/global.module';
import { GlobalErrorHandler } from './theme/utils/error-handler';

// const initializeAppConfigFactory = (appConfig: AppConfigService) => {
//   return () => {
//     return appConfig.loadAppConfig();
//   };
// };

const initializeAppFactory = (configService: ConfigService, appConfig: AppConfigService, router: Router) => {
  return async () => {
    try {
      // Initialize environment config
      const config = await configService.getConfig().toPromise();
      Object.assign(environment, config);

      // Load app-specific config and dynamically load modules
      const moduleConfig = await appConfig.loadAppConfig();
      const modulePathConfig = await appConfig.loadModule();
      console.log(modulePathConfig);

      if (modulePathConfig) {
        const domainRoutes: Routes = [
          {
            path: '',
            component: GlobalComponent,
            resolve: { store: StoreService, featureTag: FeatureTagResolver },
            children: [
              {
                path: '',
                loadChildren: () => import(`${modulePathConfig.path}`).then(m => m[modulePathConfig.name]),
                resolve: { store: StoreService, featureTag: FeatureTagResolver }
              }
            ]
          }
        ];

        // Update the router configuration
        const currentRoutes = Array.isArray(router.config) ? router.config : [];
        router.resetConfig([...currentRoutes, ...domainRoutes]);
      } else {
        console.error('No module configuration found for the current domain or theme type');
      }
    } catch (error) {
      console.error('Error during app initialization:', error);
    }
  };
};



@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    BrowserModule.withServerTransition({ appId: 'routeworx' }),
    TransferHttpCacheModule,
    BrowserTransferStateModule,
    BrowserAnimationsModule,
    HttpClientModule,
    NgxSpinnerModule,
    AgmCoreModule.forRoot({
      apiKey: environment.googleMapApi,
      libraries: ['places']
    }),
    AppRoutingModule,
    MatDialogModule,
    FlexLayoutModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    GlobalModule
  ],
  declarations: [
    AppComponent,
    MenuComponent,
    BreadcrumbComponent,
    OptionsComponent,
    CmsPageComponent,
    CmsTopBgComponent,
    CmsBottomComponent,
    // GlobalComponent,
    
  ],
  providers: [
    AppSettings,
    NotificationService,
    SubscriptionService,
    AuthGuard,
    SanitizeHtmlPipe,
    DecimalPipe,
    CurrencyPipe,
    UpperCasePipe,
    ReplacePipe,
    LoggedInAuthGuard,
    VerifyTokenGuard,
    FeatureTagResolver,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    { provide: MAT_MENU_SCROLL_STRATEGY, useFactory: menuScrollStrategy, deps: [Overlay] },
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [ConfigService, AppConfigService, Router],
      multi: true
    }
    // ConfigService,
   
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }